<template>
  <div>
    <mu-card id="commentForm" v-if="userId !== false">
      <mu-card-header titleClass="addCommentTitle" :title="'Add a comment'"></mu-card-header>
      <mu-card-text>
        <form name="commentForm" v-on:submit.prevent="addComment">
          <mu-text-field
            class="addCommentInput"
            v-model="addCommentModel.message"
            hintText="Add a comment"
            multiLine
            fullWidth
            :rows="3"
            :rowsMax="6"
            :underlineShow="false"
          />
          <div class="addComment" align="right">
            <button type="submit" style="display: none;" />
            <mu-raised-button
              fullWidth
              @click.native="addComment" type="button"
              :label="$t('message.addCommentText')"
              :disabled="isAddingComment"
            />
          </div>
        </form>
      </mu-card-text>
    </mu-card>
    <mu-flexbox style="height: 100%;" align="center" justify="flex-start" v-if="userId !== false">
      <mu-flexbox-item grow>
        <h2
          class="commentsTitle"
          v-html="$sanitize('Comments ' + ' <span>&middot</span> ' + commentParams.totalComments)"></h2>
      </mu-flexbox-item>
    </mu-flexbox>
    <mu-card class="noComments" v-if="comments.length === 0 && !isCommentsLoading">
      <mu-card-text>
        No comments yet
      </mu-card-text>
    </mu-card>
    <mu-content-block v-if="isCommentsLoading" style="text-align: center">
      <mu-circular-progress :size="32"></mu-circular-progress>
      <br/>
      <span v-text="$t('message.loadingText')"></span>
    </mu-content-block>
    <mu-card v-if="!isCommentsLoading" class="comment" v-for="(comment, commentIndex) in comments" :key="'comment-' + commentIndex">
      <mu-card-header style="padding-left: 0px;">
        <div class="mu-card-header-title" style="padding-right: 40px;">
          <div class="mu-card-title">
            <div class="titleBlock">
              <router-link
                :to="'/profile/' + comment.user.profile_url"
                class="name"
                v-text="comment.user.display_name"
              ></router-link>
              <div class="dateBadgeWrap">
                <div class="badges">
                  <user-badge v-if="comment.user.is_admin" mode="text" type="support"/>
                  <user-badge v-if="comment.user.is_complete" mode="text" type="finished-program"/>
                  <user-badge v-if="comment.user.is_champion" type="champion" mode="text" />
                </div>
                <span class="date" v-text="time(comment.timestamp)"></span>
              </div>
            </div>
          </div>
        </div>
        <mu-avatar
          v-if="!containsAvatarMed(comment.user.profile_large, 'avatar-med') && comment.user.profile_large != null"
          :src="comment.user.profile_large"
          slot="avatar"
        />
        <mu-avatar
          v-if="containsAvatarMed(comment.user.profile_large, 'avatar-med') || comment.user.profile_large === null"
          slot="avatar"
        >
          {{getInitialsFromName(comment.user.display_name, ' ')}}
        </mu-avatar>
      </mu-card-header>
      <mu-card-text style="padding-left: 0px;">
        <div class="desc">
          <vue-markdown :source="comment.comment" v-show="!comment.editcomment"></vue-markdown>
          <div class="editable" v-show="comment.editcomment">
            <mu-text-field
              v-model="comment.edit_comment"
              multiLine
              fullWidth
              :rows="3"
              :rowsMax="6">
            </mu-text-field>
            <mu-flat-button
              @click="closeReply(0, commentIndex)"
              :label="$t('message.cancelText')"
              class="demo-flat-button"
              primary
            />
            <mu-flat-button @click="updateComment(0, comment, commentIndex)" label="Save edit" class="demo-flat-button" secondary/>
          </div>
        </div>
        <report v-if="comment.report_count > 0 && isAdmin" type="LIFE_REPLY" :typeId="comment.comment_id" />
      </mu-card-text>
      <mu-card-actions>
        <div class="buttonsWrap">
          <div>
            <mu-flat-button
              v-on:click="likeTopComment(comment.comment_id, commentIndex)"
              class="likeButton"
              v-bind:class="{'active': comment.likes.liked === true}"
            >
              <div class="likeInfo">
                <font-awesome-icon icon="thumbs-up"></font-awesome-icon>
                <span class="like-label" v-text="comment.likes.liked ? 'Unlike' : 'Like'"></span>
    <!--            <span class="like-count" v-if="comment.total_likes" v-text="comment.total_likes"></span>-->
              </div>
            </mu-flat-button>
            <mu-flat-button
                class="editButton"
                v-if="(comment.user.user_id === $store.state.user.scope.user_id)"
                @click="editComment(0, commentIndex, null)"
              >
                <div class="editInfo">
                  <font-awesome-icon icon="pencil-alt"></font-awesome-icon>
                  <span>Edit</span>
                </div>
            </mu-flat-button>
            <mu-flat-button
              class="deleteButton"
              v-if="(comment.user.user_id === $store.state.user.scope.user_id) || (isAdmin)"
              @click="deleteComment(0, comment.comment_id, commentIndex, null)"
            >
              <div class="deleteInfo">
                <font-awesome-icon icon="trash"></font-awesome-icon>
                <span>Delete</span>
              </div>
            </mu-flat-button>
          </div>
          <div class="reportWrap">
            <mu-flat-button
              class="reportButton"
              v-if="(comment.user.user_id != $store.state.user.scope.user_id) && ( !isAdmin )"
              @click="openReport('LIFE_REPLY', comment.comment_id)"
              :disabled="comment.reported"
            >
              <div class="reportInfo">
                <font-awesome-icon icon="exclamation-triangle"></font-awesome-icon>
                <span v-text="comment.reported ? 'Reported' : 'Report'"></span>
              </div>
            </mu-flat-button>
          </div>
        </div>
      </mu-card-actions>
      <div class="likeCounter" v-if="comment.total_likes !== 0">
        <font-awesome-icon icon="thumbs-up"></font-awesome-icon>
        <span><strong>{{ comment.total_likes }}</strong> people liked this comment</span>
      </div>
      <form class="addReply" name="commentForm" v-on:submit.prevent="addSubComment(comment)">
        <mu-flexbox align="flex-start" style="height: 100%;" wrap="nowrap">
          <mu-flexbox-item grow="5">
            <h3 class="replyTo">Reply to {{comment.user.display_name}}'s comment</h3>
            <mu-text-field
              class="replyInput"
              v-model="comment.new_comment"
              :hintText="`Reply to ` + comment.user.display_name + `'s comment.`"
              multiLine
              fullWidth
              :rows="3"
              :rowsMax="6"
              required
              :underlineShow="false"
            />
            <button type="submit" style="display: none;" />
            <mu-flat-button
              class="replyButton"
              label="Add reply"
              type="button"
              @click.native="addSubComment(comment)"
            />
          </mu-flexbox-item>
        </mu-flexbox>
      </form>
      <mu-list v-if="comment.replies">
        <div class="subComment" v-for="(sub, subCommentIndex) in comment.replies" :key="'subComment-' + subCommentIndex">
          <mu-list-item disableRipple class="allowSelection">
            <div slot="title" style="display: flex;flex-direction: row;align-items:center;">
              <mu-avatar
                v-if="!containsAvatarMed(sub.user.profile_large, 'avatar-med') && sub.user.profile_large != null"
                :src="sub.user.profile_large"
                style="margin-right: 18px;"
              />
              <mu-avatar
                v-if="containsAvatarMed(sub.user.profile_large, 'avatar-med') || sub.user.profile_large === null"
                style="margin-rt: 18px;"
              >
                {{getInitialsFromName(sub.user.display_name, ' ')}}
              </mu-avatar>
              <router-link
                class="name blueTitle"
                :to="'/profile/' + sub.user.profile_url"
                v-text="sub.user.display_name"
              ></router-link>
              <div class="dateBadgeWrap">
                <div class="badges">
                  <user-badge v-if="sub.user.is_admin" mode="text" type="support"/>
                  <user-badge v-if="sub.user.is_complete" mode="text" type="finished-program"/>
                  <user-badge v-if="sub.user.is_champion" type="champion" mode="text" />
                </div>
                <span class="date" v-text="time(sub.timestamp)"></span>
              </div>
            </div>
            <div class="desc" slot="describe">
              <vue-markdown :source="sub.comment" v-show="!sub.editcomment"></vue-markdown>
              <div class="editable" v-show="sub.editcomment">
                <mu-text-field
                  v-model="sub.edit_comment"
                  multiLine
                  fullWidth
                  :rows="3"
                  :rowsMax="6">
                </mu-text-field>
                <mu-flat-button
                  @click="closeReply(1, subCommentIndex, commentIndex)"
                  :label="$t('message.cancelText')"
                  class="demo-flat-button"
                  primary
                />
                <mu-flat-button @click="updateComment(1, sub, subCommentIndex, commentIndex)" label="Save edit" class="demo-flat-button"
                                secondary/>
              </div>
            </div>
            <report v-if="sub.report_count > 0 && isAdmin" type="LIFE_SUBREPLY" :typeId="sub.sub_comment_id" />
            <mu-card-actions class="actions">
              <div class="buttonsWrap">
                <div>
                  <mu-flat-button
                    v-on:click="likeSubComment(sub.comment_id, sub.sub_comment_id, commentIndex, subCommentIndex)"
                    class="likeButton"
                    v-bind:class="{'active': sub.likes.liked === true}"
                  >
                    <div class="likeInfo">
                      <font-awesome-icon icon="thumbs-up"></font-awesome-icon>
                      <span class="like-label" v-text="sub.likes.liked ? 'Unlike' : 'Like'"></span>
                    </div>
                  </mu-flat-button>
                  <mu-flat-button
                    class="editButton"
                    v-if="(sub.user.user_id === $store.state.user.scope.user_id)"
                    @click.native="editComment(1, subCommentIndex, commentIndex)"
                  >
                    <div class="editInfo">
                      <font-awesome-icon icon="pencil-alt"></font-awesome-icon>
                      <span>Edit</span>
                    </div>
                  </mu-flat-button>
                  <mu-flat-button
                    class="deleteButton"
                    v-if="(sub.user.user_id === $store.state.user.scope.user_id) || (isAdmin)"
                    @click.native="deleteComment(1, sub.sub_comment_id, subCommentIndex, commentIndex)"
                  >
                    <div class="deleteInfo">
                      <font-awesome-icon icon="trash"></font-awesome-icon>
                      <span>Delete</span>
                    </div>
                  </mu-flat-button>
                </div>
                <div class="reportWrap">
                  <mu-flat-button
                    class="reportButton"
                    v-if="(sub.user.user_id != $store.state.user.scope.user_id) && ( !isAdmin )"
                    @click="openReport('LIFE_SUBREPLY', sub.sub_comment_id)"
                    :disabled="sub.reported"
                  >
                    <div class="reportInfo">
                      <font-awesome-icon icon="exclamation-triangle"></font-awesome-icon>
                      <span v-text="sub.reported ? 'Reported' : 'Report'"></span>
                    </div>
                  </mu-flat-button>
                </div>
              </div>
            </mu-card-actions>
            <div class="likeCounter" v-if="sub.total_likes !== 0">
              <font-awesome-icon icon="thumbs-up"></font-awesome-icon>
              <span><strong>{{ sub.total_likes }}</strong> people liked this comment</span>
            </div>
          </mu-list-item>
        </div>
      </mu-list>
    </mu-card>
    <mu-card-actions
      class="load-more alignCenter"
      v-show="!isCommentsComplete || userId !== false">
      <mu-raised-button
        v-if="commentParams.totalComments > commentParams.page_size"
        v-on:click="loadPostComments(postId)"
        :label="(isCommentsLoading) ? $t('message.loadingText') : $t('message.loadCommentsText')"
        :disabled="isCommentsComplete || isCommentsLoading"
        secondary
      ></mu-raised-button>
    </mu-card-actions>
    <report-modal
      v-if="activeReport.type && activeReport.type_id"
      :open="activeReport.open"
      :type="activeReport.type"
      :type_id="activeReport.type_id"
      @onClose="activeReport.open = false"
      @onComplete="setReported(activeReport)">
    </report-modal>
  </div>
</template>

<script>
  import LifeCommentsMixin from './LifeCommentsMixin.js';
  import ScrollerMixin from '../../../mixins/ScrollerMixin'
  import ReportingMixin from '../../../mixins/ReportingMixin'
  import ReportModal from "../../../components/Reporting/Reporting.vue"
  import Report from "../../Reporting/Report";
  import UserBadge from "../../UserBadge/UserBadge.vue";
  export default {
    components: {
      UserBadge,
      Report,
      ReportModal,
    },
    name: 'life-comments',
    mixins: [
      LifeCommentsMixin,
      ScrollerMixin,
      ReportingMixin
    ],
    props: {
      postId: {
        type: Number,
        required: true
      },
      userId: {
        type: Number,
        required: true
      }
    },
    methods: {
      time(date) {
        return moment(date).format('MMMM Do YYYY [at] h:mma');
      },
      setReported() {
        let self = this,
            currentThread;
        if(self.activeReport.type === 'LIFE_REPLY') {
          currentThread = self.comments.filter(function (item) {
            return item.comment_id === self.activeReport.type_id;
          })[0];
        } else if (self.activeReport.type === 'LIFE_SUBREPLY') {
          var i = 0;
          for(i in self.comments) {
            currentThread = self.comments[i].replies.filter(function (item) {
              return item.sub_comment_id === self.activeReport.type_id;
            })[0];
          }
        }
        currentThread.reported = true;
        self.$forceUpdate();
      }
    }
  }
</script>



// WEBPACK FOOTER //
// LifeComments.vue?2dabd6d3